import styled from '@emotion/styled';
import React from 'react';

import specialtiesIcon from 'src/public/img/icon_coaching_specialty.svg';
import locationIcon from 'src/public/img/icon_provider_location.svg';
import languagesIcon from 'src/public/img/icon_provider_languages.svg';
import { colors as dsColors } from '@modernhealth/vital-foundation';
import { BodyTextSmall, HeadingText40, Modal, pxToRem } from '@modernhealth/vital-components';
import { CoachNode } from 'src/generated/webapp_gql';
import { ProviderType } from 'src/modules/user/models/user';
import { formatProviderLocation } from 'src/modules/providers/components/ProviderCard';

import { IntakeFormTestID } from 'src/modules/intakeForm/models/intakeForm';
import { CoachIntroWrapper } from 'src/modules/providers/components/CoachIntroWrapper';

const ModalBodyWrapper = styled.div<{ withTitle: boolean }>(({ withTitle }) => ({
    padding: pxToRem(16),
    border: `${pxToRem(1.5)} solid ${dsColors.gray05}`,
    borderRadius: pxToRem(4),
    margin: pxToRem(32),
    marginTop: pxToRem(withTitle ? 0 : 64),
    maxHeight: '75vh',
    overflow: 'auto',
}));

const IconWrapper = styled.div({
    display: 'flex',
    marginTop: pxToRem(18),
    alignItems: 'center',
});

const Icon = styled.img({
    height: pxToRem(24),
    width: pxToRem(24),
    marginRight: pxToRem(10),
});

interface Props {
    isVisible: boolean;
    coach?: Partial<CoachNode>;
    closeCoachMoreInfoModal: () => void;
    title?: string;
}

export const CoachMoreInfoModal: React.FC<Props> = ({
    coach,
    isVisible,
    closeCoachMoreInfoModal,
    title,
}) => {
    const location = formatProviderLocation(
        coach?.country,
        coach?.city,
        coach?.state,
        coach?.zipCode,
        coach?.providerType,
    );
    return (
        <Modal
            isVisible={isVisible}
            onCloseButtonClick={closeCoachMoreInfoModal}
            maxWidth={700}
            disableHeightAnimation
            noPaddings
        >
            {title && (
                <HeadingText40 marginTop={40} marginBottom={24} centered>
                    {title}
                </HeadingText40>
            )}
            <ModalBodyWrapper data-testid={IntakeFormTestID.CoachMoreInfoModal} withTitle={!!title}>
                <CoachIntroWrapper
                    coachAvatar={coach?.avatar || ''}
                    coachCredentials={coach?.providerType || ProviderType.COACH}
                    coachFirstName={coach?.firstName || ''}
                    coachLastName={coach?.lastName || ''}
                />

                {coach?.providerSpecialties && (
                    <IconWrapper>
                        <Icon src={specialtiesIcon} alt="specialties-icon" />
                        <BodyTextSmall fontColor="gray65">
                            {(coach?.providerSpecialties || []).join(', ')}
                        </BodyTextSmall>
                    </IconWrapper>
                )}
                {coach?.languagePreferences && (
                    <IconWrapper>
                        <Icon src={languagesIcon} alt="languages-icon" />
                        <BodyTextSmall fontColor="gray65">
                            {(coach?.languagePreferences || []).join(', ')}
                        </BodyTextSmall>
                    </IconWrapper>
                )}
                {location && (
                    <IconWrapper>
                        <Icon src={locationIcon} alt="locations-icon" />
                        <BodyTextSmall fontColor="gray65">{location}</BodyTextSmall>
                    </IconWrapper>
                )}

                <BodyTextSmall fontColor="gray65" marginTop={18}>
                    {coach?.introduction}
                </BodyTextSmall>
            </ModalBodyWrapper>
        </Modal>
    );
};
